import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { createFileRoute, useRouter, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  BadgeCheck,
  MoreHorizontal,
  Pencil,
  Plus,
  Tag,
  Undo2,
  Wrench,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { Input } from '@/components/atoms/input';
import { Switch } from '@/components/atoms/switch';
import { StatusChip } from '@/components/molecules/StatusChip';
import { Label } from '@/components/atoms/label';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/sheet';
import {
  CollapsibleSectionHeader,
  DataTableDrawerHeader,
  DataTableSearchFilterField,
  DataTableSelectFilterField,
  FormAccordionCard,
} from '@/components/molecules';
import {
  DataTableFilterSection,
  SettingsDataGrid,
  type SettingsDataGridColumn,
  type SettingsDataGridSortDir,
} from '@/components/organisms';
import { DetailPageLayout } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import { apiGet, apiPatch, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import { useTrades } from '@/hooks/scheduler/useTrades';
import type { ServiceType } from '@/types/scheduler';
import {
  EMPTY_SKILL_FORM as EMPTY_FORM,
  slugify,
  useCreateSkill,
  type SkillFormValues,
} from '@/components/organisms/skills/skill-form-shared';

export const Route = createFileRoute('/_dashboard/dashboard/settings/skills')({
  component: SkillsPage,
});

const PAGE_SIZE = 25;
const SKILL_NEW_ROUTE_PATTERN = /^\/dashboard\/settings\/skills\/new\/?$/;
const SKILL_DETAIL_ROUTE_PATTERN = /^\/dashboard\/settings\/skills\/([^/]+)\/?$/;
const SKILL_EDIT_ROUTE_PATTERN = /^\/dashboard\/settings\/skills\/([^/]+)\/edit\/?$/;

type SkillColumn = 'skill' | 'certification' | 'status';
type SkillStatusFilter = 'active' | 'archived' | 'all';

const SKILL_COLUMNS: SettingsDataGridColumn<SkillColumn>[] = [
  { key: 'skill', label: 'Skill', sortable: true, width: 360 },
  { key: 'certification', label: 'Certification', sortable: true, width: 200 },
  { key: 'status', label: 'Status', sortable: true, align: 'center', width: 120 },
];

const STATUS_OPTIONS: { value: SkillStatusFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

// Deferred-apply drawer filter (NOT-391): the Status select edits a local draft;
// filtering only changes on Apply. Text search stays instant (not drafted).
interface SkillFilterDraft {
  search: string;
  status: SkillStatusFilter;
}

const SKILL_FILTER_DEFAULTS: SkillFilterDraft = {
  search: '',
  status: 'active',
};

function SkillStatusBadge({
  active,
  variant = 'badge',
}: {
  active: boolean;
  variant?: 'badge' | 'table';
}) {
  return (
    <StatusChip
      status={active ? 'ACTIVE' : 'INACTIVE'}
      label={active ? 'Active' : 'Archived'}
      variant={variant}
    />
  );
}

function getSortValue(column: SkillColumn, skill: ServiceType) {
  switch (column) {
    case 'skill':
      return skill.name.toLowerCase();
    case 'certification':
      // Non-certs first, then certs ordered by validity (0 = no validity).
      return skill.isCertification ? 1000 + (skill.defaultValidityMonths ?? 0) : 0;
    case 'status':
      return skill.isActive ? 'active' : 'archived';
  }
}

function compareValues(a: string | number, b: string | number) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

function SkillsPage() {
  const activeTradesQuery = useTrades('active');
  const activeTrades = activeTradesQuery.data ?? [];
  const hasNoTrades = activeTradesQuery.isSuccess && activeTrades.length === 0;
  const router = useRouter();
  const { location } = useRouterState();
  const { data: skills = [], isLoading } = useApiQuery<ServiceType[]>({
    queryKey: [...schedulerKeys.serviceTypes(), 'all'],
    queryFn: () =>
      apiGet<ServiceType[]>(operationsClient, `${schedulerEndpoints.serviceTypes()}?status=all`),
  });

  const createSkill = useCreateSkill();
  const updateSkill = useUpdateSkill();
  const archiveSkill = useArchiveSkill();

  const createOpen = SKILL_NEW_ROUTE_PATTERN.test(location.pathname);
  const editSkillId = location.pathname.match(SKILL_EDIT_ROUTE_PATTERN)?.[1];
  const decodedEditSkillId = editSkillId ? decodeURIComponent(editSkillId) : undefined;
  const editSkill = useMemo(
    () => skills.find((skill) => skill.id === decodedEditSkillId) ?? null,
    [decodedEditSkillId, skills],
  );

  const detailSkillId =
    !createOpen && !editSkillId
      ? location.pathname.match(SKILL_DETAIL_ROUTE_PATTERN)?.[1]
      : undefined;
  const decodedDetailSkillId = detailSkillId ? decodeURIComponent(detailSkillId) : undefined;
  const detailSkill = useMemo(
    () => skills.find((skill) => skill.id === decodedDetailSkillId) ?? null,
    [decodedDetailSkillId, skills],
  );

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SkillStatusFilter>('active');
  const [sortKey, setSortKey] = useState<SkillColumn | null>('skill');
  const [sortDir, setSortDir] = useState<SettingsDataGridSortDir>('asc');
  const [page, setPage] = useState(1);
  const [archiveTarget, setArchiveTarget] = useState<ServiceType | null>(null);

  const filteredSkills = useMemo(() => {
    const q = search.trim().toLowerCase();
    return skills.filter((skill) => {
      const matchesSearch = !q || [skill.name, skill.slug].join(' ').toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && skill.isActive) ||
        (statusFilter === 'archived' && !skill.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [search, skills, statusFilter]);

  const sortedSkills = useMemo(() => {
    if (!sortKey) return filteredSkills;
    return [...filteredSkills].sort((a, b) => {
      const result = compareValues(getSortValue(sortKey, a), getSortValue(sortKey, b));
      return sortDir === 'asc' ? result : -result;
    });
  }, [filteredSkills, sortDir, sortKey]);

  const activeFilterCount = statusFilter !== 'active' ? 1 : 0;
  const totalPages = Math.max(1, Math.ceil(sortedSkills.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageSkills = sortedSkills.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const drawerPending = createSkill.isPending || updateSkill.isPending;

  function resetFilters() {
    setSearch('');
    setStatusFilter('active');
    setPage(1);
  }

  function handleSort(column: SettingsDataGridColumn<SkillColumn>, dir: SettingsDataGridSortDir) {
    if (!column.sortable) return;
    setSortKey(column.key);
    setSortDir(dir);
    setPage(1);
  }

  function openCreateDrawer() {
    void router.navigate({ to: '/dashboard/settings/skills/new' });
  }

  function openDetailDrawer(skillId: string) {
    void router.navigate({
      to: '/dashboard/settings/skills/$id',
      params: { id: skillId },
    });
  }

  function openEditDrawer(skillId: string) {
    void router.navigate({
      to: '/dashboard/settings/skills/$id/edit',
      params: { id: skillId },
    });
  }

  function closeDrawer() {
    void router.navigate({ to: '/dashboard/settings/skills' });
  }

  function handleCreate(values: SkillFormValues) {
    createSkill.mutate(
      {
        name: values.name.trim(),
        slug: values.slug.trim(),
        tradeId: hasNoTrades ? null : values.tradeId,
        isCertification: values.isCertification,
        defaultValidityMonths: values.isCertification ? values.defaultValidityMonths : null,
      },
      { onSuccess: closeDrawer },
    );
  }

  function handleUpdate(skillId: string, values: SkillFormValues) {
    updateSkill.mutate(
      {
        id: skillId,
        dto: {
          name: values.name.trim(),
          ...(hasNoTrades ? {} : { tradeId: values.tradeId }),
          isCertification: values.isCertification,
          defaultValidityMonths: values.isCertification ? values.defaultValidityMonths : null,
        },
      },
      { onSuccess: closeDrawer },
    );
  }

  function renderCell(key: SkillColumn, skill: ServiceType) {
    switch (key) {
      case 'skill':
        return (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{skill.name}</p>
            <p className="truncate text-xs text-muted-foreground">{skill.slug}</p>
          </div>
        );
      case 'certification':
        return skill.isCertification ? (
          <span className="inline-flex items-center gap-1.5 text-sm">
            <BadgeCheck className="h-3.5 w-3.5 text-muted-foreground" />
            Certification
            {skill.defaultValidityMonths ? (
              <span className="text-muted-foreground">· {skill.defaultValidityMonths} mo</span>
            ) : null}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      case 'status':
        return <SkillStatusBadge active={skill.isActive} variant="table" />;
    }
  }

  // Drawer select writes to a draft; committed statusFilter still drives the list
  // and the activeFilterCount badge. Search stays instant (not drafted).
  const committedFilters = useMemo<SkillFilterDraft>(
    () => ({ search, status: statusFilter }),
    [search, statusFilter],
  );
  const filterDraft = useFilterDraft(
    committedFilters,
    (next) => {
      setSearch(next.search);
      setStatusFilter(next.status);
      setPage(1);
    },
    SKILL_FILTER_DEFAULTS,
  );
  const { draft } = filterDraft;
  const draftFilterCount = (draft.search.trim() ? 1 : 0) + (draft.status !== 'active' ? 1 : 0);

  const filterContent = (
    <>
      <DataTableFilterSection
        label="Skill"
        active={!!draft.search}
        onClear={() => filterDraft.setDraft({ search: '' })}
      >
        <DataTableSearchFilterField
          value={draft.search}
          onValueChange={(value) => filterDraft.setDraft({ search: value })}
          placeholder="Search by name or slug..."
        />
      </DataTableFilterSection>
      <DataTableFilterSection
        label="Status"
        active={draft.status !== 'active'}
        onClear={() => filterDraft.setDraft({ status: 'active' })}
      >
        <DataTableSelectFilterField
          value={draft.status}
          onValueChange={(value) => filterDraft.setDraft({ status: value as SkillStatusFilter })}
          placeholder="Active"
          options={STATUS_OPTIONS}
        />
      </DataTableFilterSection>
    </>
  );

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">Skills</h1>
              <p className="text-xs text-muted-foreground">
                Manage the skills used for scheduling and technician expertise.
              </p>
            </div>
            <Button size="sm" onClick={openCreateDrawer}>
              <Plus className="h-4 w-4" />
              Add skill
            </Button>
          </>
        }
        contentClassName="flex min-h-0 flex-col p-0"
      >
        <SettingsDataGrid
          columns={SKILL_COLUMNS}
          rows={pageSkills}
          getRowKey={(skill) => skill.id}
          renderCell={(key, skill) => renderCell(key, skill)}
          storageKey="settings-skills"
          emptyIcon={Wrench}
          emptyTitle={skills.length === 0 ? 'No skills yet' : 'No matching skills found'}
          searchValue={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          searchPlaceholder="Search skills..."
          showClearAll={Boolean(search) || activeFilterCount > 0}
          onClearAll={resetFilters}
          activeFilterCount={activeFilterCount}
          filterSheet={{
            onReset: filterDraft.reset,
            resetDisabled: draftFilterCount === 0,
            onApply: filterDraft.apply,
            onOpen: filterDraft.seed,
            content: filterContent,
          }}
          showExport={false}
          isLoading={isLoading}
          skeletonRowCount={8}
          onRowClick={(skill) => openDetailDrawer(skill.id)}
          renderRowActions={(skill) => (
            <SkillRowActions
              skill={skill}
              onEdit={() => openEditDrawer(skill.id)}
              onArchive={() => {
                if (skill.isActive) {
                  setArchiveTarget(skill);
                  return;
                }
                archiveSkill.mutate({ id: skill.id, isActive: true });
              }}
            />
          )}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          pagination={{
            count: sortedSkills.length,
            singular: 'skill',
            page: currentPage,
            totalPages,
            onPrevious: () => setPage((current) => Math.max(1, current - 1)),
            onNext: () => setPage((current) => Math.min(totalPages, current + 1)),
          }}
        />
      </DetailPageLayout>

      <Sheet
        open={createOpen || Boolean(decodedDetailSkillId) || Boolean(decodedEditSkillId)}
        onOpenChange={(open) => {
          if (!open) closeDrawer();
        }}
      >
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
          showCloseButton={false}
        >
          {createOpen || decodedEditSkillId ? (
            <SkillFormContent
              mode={createOpen ? 'create' : 'edit'}
              skill={editSkill}
              isLoading={Boolean(decodedEditSkillId) && isLoading}
              isPending={drawerPending}
              onClose={closeDrawer}
              onBack={decodedEditSkillId ? () => openDetailDrawer(decodedEditSkillId) : undefined}
              onCreate={handleCreate}
              onUpdate={handleUpdate}
            />
          ) : detailSkill ? (
            <SkillDetailContent
              skill={detailSkill}
              onEdit={() => openEditDrawer(detailSkill.id)}
              onArchive={() => {
                if (detailSkill.isActive) {
                  setArchiveTarget(detailSkill);
                  return;
                }
                archiveSkill.mutate({ id: detailSkill.id, isActive: true });
              }}
              isArchivePending={archiveSkill.isPending}
              onClose={closeDrawer}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      <Dialog
        open={Boolean(archiveTarget)}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive skill?</DialogTitle>
            <DialogDescription>
              {archiveTarget ? (
                <>
                  Archive <strong>{archiveTarget.name}</strong>? It disappears from new assignments
                  and job-type requirements. Technicians and job types that already reference it
                  keep it.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={archiveSkill.isPending}
              onClick={() => setArchiveTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={archiveSkill.isPending}
              onClick={() => {
                if (!archiveTarget) return;
                archiveSkill.mutate(
                  { id: archiveTarget.id, isActive: false },
                  {
                    onSuccess: () => setArchiveTarget(null),
                  },
                );
              }}
            >
              {archiveSkill.isPending ? 'Archiving...' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SkillRowActions({
  skill,
  onEdit,
  onArchive,
}: {
  skill: ServiceType;
  onEdit: () => void;
  onArchive: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant={skill.isActive ? 'destructive' : undefined} onSelect={onArchive}>
          {skill.isActive ? (
            <Archive className="h-3.5 w-3.5" />
          ) : (
            <ArchiveRestore className="h-3.5 w-3.5" />
          )}
          {skill.isActive ? 'Archive' : 'Restore'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SkillDetailContent({
  skill,
  onEdit,
  onArchive,
  isArchivePending,
  onClose,
}: {
  skill: ServiceType;
  onEdit: () => void;
  onArchive: () => void;
  isArchivePending: boolean;
  onClose: () => void;
}) {
  return (
    <>
      <SheetHeader className="border-b px-5 py-4">
        <div className="flex items-center justify-between">
          <SheetTitle className="text-sm font-semibold">{skill.name}</SheetTitle>
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" className="h-8 w-8" title="Edit" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              className="h-8 w-8"
              title={skill.isActive ? 'Archive' : 'Restore'}
              aria-label={skill.isActive ? 'Archive skill' : 'Restore skill'}
              disabled={isArchivePending}
              onClick={onArchive}
            >
              {skill.isActive ? (
                <Archive className="h-3.5 w-3.5 text-destructive" />
              ) : (
                <ArchiveRestore className="h-3.5 w-3.5" />
              )}
            </Button>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X size={16} />
            </button>
          </div>
        </div>
      </SheetHeader>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
        <FormAccordionCard
          open
          header={
            <CollapsibleSectionHeader icon={Tag} label="Details" open onToggle={() => undefined} />
          }
        >
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Name</span>
              <span className="font-medium">{skill.name}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Slug</span>
              <span className="font-mono text-xs text-muted-foreground">{skill.slug}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Status</span>
              <SkillStatusBadge active={skill.isActive} />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Certification</span>
              {skill.isCertification ? (
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <BadgeCheck className="h-3.5 w-3.5 text-muted-foreground" />
                  Yes
                </span>
              ) : (
                <span className="font-medium">No</span>
              )}
            </div>
            {skill.isCertification && skill.defaultValidityMonths ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Default validity</span>
                <span className="tabular-nums">{skill.defaultValidityMonths} months</span>
              </div>
            ) : null}
          </div>
        </FormAccordionCard>
      </div>
    </>
  );
}

export function SkillFormContent({
  mode,
  skill,
  isLoading,
  isPending,
  onClose,
  onBack,
  onCreate,
  onUpdate,
  initialValues,
}: {
  mode: 'create' | 'edit';
  skill: ServiceType | null;
  isLoading: boolean;
  isPending: boolean;
  onClose: () => void;
  onBack?: () => void;
  onCreate: (values: SkillFormValues) => void;
  onUpdate: (skillId: string, values: SkillFormValues) => void;
  /** Create-mode overrides (e.g. open with the certification toggle on). */
  initialValues?: Partial<SkillFormValues>;
}) {
  const [form, setForm] = useState<SkillFormValues>({ ...EMPTY_FORM, ...initialValues });
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugEditing, setSlugEditing] = useState(false);
  // Snapshot of { slug, touched } taken when entering slug edit, so Esc can restore it.
  const preEditSlugRef = useRef<{ slug: string; touched: boolean }>({ slug: '', touched: false });

  // Esc while editing the slug = cancel (restore the pre-edit value), NOT dismiss the
  // Sheet/Dialog. Radix dismisses on a document-level Escape listener and the content is
  // portaled, so a React handler on the input can't stop it. A window capture-phase
  // listener fires first, so we cancel + swallow the event before Radix sees it.
  useEffect(() => {
    if (!slugEditing) return;
    function onEscapeCapture(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setForm((current) => ({ ...current, slug: preEditSlugRef.current.slug }));
      setSlugTouched(preEditSlugRef.current.touched);
      setSlugEditing(false);
    }
    window.addEventListener('keydown', onEscapeCapture, true);
    return () => window.removeEventListener('keydown', onEscapeCapture, true);
  }, [slugEditing]);
  const isEdit = mode === 'edit';

  useEffect(() => {
    if (mode === 'create') {
      setForm({ ...EMPTY_FORM, ...initialValues });
      setSlugTouched(false);
      setSlugEditing(false);
      return;
    }

    if (!skill) return;

    setForm({
      name: skill.name,
      slug: skill.slug,
      tradeId: skill.tradeId,
      isCertification: skill.isCertification ?? false,
      defaultValidityMonths: skill.defaultValidityMonths ?? null,
    });
    setSlugTouched(true);
    setSlugEditing(false);
    // `initialValues` is a fresh object each render; deps intentionally omit it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, skill]);

  function setName(name: string) {
    setForm((current) => ({
      ...current,
      name,
      slug: !isEdit && !slugTouched ? slugify(name) : current.slug,
    }));
  }

  const isValid = form.name.trim().length > 0 && (isEdit || form.slug.trim().length > 0);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // This form can be embedded inside another <form> (the inline
    // SkillCreateDialog launched from TechnicianForm). The Sheet portals the
    // DOM but React synthetic events still bubble the React tree, so without
    // this the parent technician form would submit too (PATCH + navigate).
    event.stopPropagation();
    const name = form.name.trim();
    const slug = form.slug.trim();
    if (!name) {
      toast.error('Skill name is required');
      return;
    }
    if (!isEdit && !slug) {
      toast.error('Slug is required');
      return;
    }

    const payload = { ...form, name, slug };
    if (isEdit && skill) {
      onUpdate(skill.id, payload);
      return;
    }

    onCreate(payload);
  }

  return (
    <>
      <DataTableDrawerHeader
        title={isEdit ? 'Edit Skill' : 'Create Skill'}
        onClose={onClose}
        onBack={onBack}
      />

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading skill...</p>
          ) : isEdit && !skill ? (
            <p className="text-sm text-muted-foreground">Skill not found.</p>
          ) : (
            <>
              {/* Name + auto-derived, editable slug hint. */}
              <div className="space-y-1.5">
                <Label htmlFor="skill-name">Name</Label>
                <Input
                  id="skill-name"
                  value={form.name}
                  disabled={isPending}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. AC Repair"
                />
                {isEdit ? (
                  <p className="text-xs text-muted-foreground">
                    slug <span className="font-mono">{form.slug}</span>
                  </p>
                ) : slugEditing ? (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">slug</span>
                    <Input
                      autoFocus
                      className="h-7 w-48 font-mono text-xs"
                      value={form.slug}
                      disabled={isPending}
                      onFocus={(event) => event.target.select()}
                      onChange={(event) => {
                        setSlugTouched(true);
                        setForm((current) => ({
                          ...current,
                          slug: slugify(event.target.value),
                        }));
                      }}
                      onKeyDown={(event) => {
                        // Enter confirms; value already lives in form.slug. preventDefault
                        // so it doesn't submit the surrounding form. Escape (cancel) is
                        // handled by a window-capture listener — see the effect above —
                        // because Radix dismisses the Sheet on a document Escape listener
                        // that a React handler here can't beat.
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          setSlugEditing(false);
                        }
                      }}
                      onBlur={() => setSlugEditing(false)}
                      placeholder="ac-repair"
                    />
                    <span className="text-[11px] text-muted-foreground">
                      ↵ confirm · esc cancel
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">slug</span>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 hover:bg-muted"
                      onClick={() => {
                        preEditSlugRef.current = { slug: form.slug, touched: slugTouched };
                        setSlugEditing(true);
                      }}
                    >
                      <span className="font-mono text-foreground">{form.slug || 'auto'}</span>
                      <Pencil className="h-3 w-3 text-muted-foreground" />
                    </button>
                    {slugTouched ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                        onClick={() => {
                          setForm((current) => ({ ...current, slug: slugify(current.name) }));
                          setSlugTouched(false);
                        }}
                      >
                        <Undo2 className="h-3 w-3" />
                        Reset to name
                      </button>
                    ) : null}
                  </div>
                )}
              </div>

              {/* §7.2 — certification (hard requirement + per-tech expiry). */}
              <div className="space-y-3 rounded-lg border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label htmlFor="skill-cert">This is a certification</Label>
                    <p className="text-xs text-muted-foreground">
                      Jobs requiring it exclude techs without it or whose cert has expired.
                    </p>
                  </div>
                  <Switch
                    id="skill-cert"
                    checked={form.isCertification}
                    disabled={isPending}
                    onCheckedChange={(checked) =>
                      setForm((current) => ({ ...current, isCertification: checked }))
                    }
                  />
                </div>
                {form.isCertification ? (
                  <div className="max-w-xs space-y-1.5">
                    <Label htmlFor="skill-validity">Default validity (months)</Label>
                    <Input
                      id="skill-validity"
                      type="number"
                      min={1}
                      placeholder="e.g. 24"
                      value={form.defaultValidityMonths ?? ''}
                      disabled={isPending}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          defaultValidityMonths: event.target.value
                            ? Number(event.target.value)
                            : null,
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Pre-fills each technician&apos;s expiry date.
                    </p>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>

        <SheetFooter className="flex-row justify-end border-t px-5 py-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending || !isValid || (isEdit && !skill)}>
            {isPending ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save' : 'Create'}
          </Button>
        </SheetFooter>
      </form>
    </>
  );
}

function useUpdateSkill() {
  const queryClient = useQueryClient();
  type UpdateSkillDto = Omit<SkillFormValues, 'slug' | 'tradeId'> & {
    tradeId?: string | null;
  };

  return useApiMutation<ServiceType, { id: string; dto: UpdateSkillDto }>({
    mutationFn: ({ id, dto }) =>
      apiPatch<ServiceType, UpdateSkillDto>(
        operationsClient,
        schedulerEndpoints.serviceType(id),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.serviceTypes() });
      toast.success('Skill saved');
    },
  });
}

function useArchiveSkill() {
  const queryClient = useQueryClient();
  return useApiMutation<ServiceType, { id: string; isActive: boolean }>({
    mutationFn: ({ id, isActive }) =>
      apiPatch<ServiceType, { isActive: boolean }>(
        operationsClient,
        schedulerEndpoints.serviceType(id),
        { isActive },
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.serviceTypes() });
      toast.success(variables.isActive ? 'Skill restored' : 'Skill archived');
    },
  });
}
